/**
 * Geographic Utilities
 * Geohashing, distance calculations, and location helpers
 */

import type { GeoLocation, MapBounds } from '../types'

// Base32 alphabet for geohashing (excludes 'a', 'i', 'l', 'o' to avoid confusion)
const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz'

/**
 * Encode latitude/longitude to geohash string
 * @param lat Latitude (-90 to 90)
 * @param lon Longitude (-180 to 180)
 * @param precision Number of characters in the hash (default 9)
 * @returns Geohash string
 */
export function encodeGeohash(lat: number, lon: number, precision = 9): string {
  if (lat < -90 || lat > 90) {
    throw new Error(`Invalid latitude: ${lat}. Must be between -90 and 90.`)
  }
  if (lon < -180 || lon > 180) {
    throw new Error(`Invalid longitude: ${lon}. Must be between -180 and 180.`)
  }
  if (precision < 1 || precision > 12) {
    throw new Error(`Invalid precision: ${precision}. Must be between 1 and 12.`)
  }

  let idx = 0
  let bit = 0
  let evenBit = true
  let hash = ''
  let latMin = -90
  let latMax = 90
  let lonMin = -180
  let lonMax = 180

  while (hash.length < precision) {
    if (evenBit) {
      const lonMid = (lonMin + lonMax) / 2
      if (lon >= lonMid) {
        idx = idx * 2 + 1
        lonMin = lonMid
      } else {
        idx = idx * 2
        lonMax = lonMid
      }
    } else {
      const latMid = (latMin + latMax) / 2
      if (lat >= latMid) {
        idx = idx * 2 + 1
        latMin = latMid
      } else {
        idx = idx * 2
        latMax = latMid
      }
    }
    evenBit = !evenBit

    if (++bit === 5) {
      hash += BASE32[idx]
      bit = 0
      idx = 0
    }
  }

  return hash
}

/**
 * Decode geohash to latitude/longitude bounds
 * @param geohash Geohash string
 * @returns Object with lat/lon min/max values
 */
export function decodeGeohash(geohash: string): MapBounds {
  let evenBit = true
  let latMin = -90
  let latMax = 90
  let lonMin = -180
  let lonMax = 180

  for (const char of geohash.toLowerCase()) {
    const idx = BASE32.indexOf(char)
    if (idx === -1) {
      throw new Error(`Invalid geohash character: ${char}`)
    }

    for (let n = 4; n >= 0; n--) {
      const bitN = (idx >> n) & 1
      if (evenBit) {
        const lonMid = (lonMin + lonMax) / 2
        if (bitN === 1) {
          lonMin = lonMid
        } else {
          lonMax = lonMid
        }
      } else {
        const latMid = (latMin + latMax) / 2
        if (bitN === 1) {
          latMin = latMid
        } else {
          latMax = latMid
        }
      }
      evenBit = !evenBit
    }
  }

  return {
    north: latMax,
    south: latMin,
    east: lonMax,
    west: lonMin,
  }
}

/**
 * Calculate the Haversine distance between two points
 * @param lat1 Latitude of point 1
 * @param lon1 Longitude of point 1
 * @param lat2 Latitude of point 2
 * @param lon2 Longitude of point 2
 * @returns Distance in kilometers
 */
export function getDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371 // Earth's radius in kilometers

  const dLat = toRadians(lat2 - lat1)
  const dLon = toRadians(lon2 - lon1)

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) ** 2

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

/**
 * Convert degrees to radians
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180)
}

/**
 * Get geohash prefix for a given radius around a point
 * Smaller radius = longer prefix (more specific)
 * @param radiusKm Radius in kilometers
 * @returns Recommended geohash precision
 */
export function getGeohashPrecisionForRadius(radiusKm: number): number {
  // Approximate cell dimensions at each precision level
  const precisionToKm: Record<number, number> = {
    1: 5000,
    2: 1250,
    3: 156,
    4: 39,
    5: 5,
    6: 1.2,
    7: 0.15,
    8: 0.038,
    9: 0.005,
  }

  for (let precision = 1; precision <= 9; precision++) {
    if ((precisionToKm[precision] ?? 5000) <= radiusKm * 2) {
      return precision
    }
  }

  return 9
}

/**
 * Get neighboring geohashes for a given geohash
 * Useful for queries that need to check adjacent cells
 * @param geohash Center geohash
 * @returns Array of 8 neighboring geohashes + center
 */
export function getGeohashNeighbors(geohash: string): string[] {
  const bounds = decodeGeohash(geohash)
  const centerLat = (bounds.north + bounds.south) / 2
  const centerLon = (bounds.east + bounds.west) / 2
  const latDelta = bounds.north - bounds.south
  const lonDelta = bounds.east - bounds.west

  const neighbors: string[] = [geohash]

  // 8 directions: N, NE, E, SE, S, SW, W, NW
  const directions = [
    [1, 0],
    [1, 1],
    [0, 1],
    [-1, 1],
    [-1, 0],
    [-1, -1],
    [0, -1],
    [1, -1],
  ]

  for (const [latDir, lonDir] of directions) {
    const lat = centerLat + (latDir ?? 0) * latDelta
    const lon = centerLon + (lonDir ?? 0) * lonDelta

    // Wrap longitude around if needed
    const wrappedLon = lon > 180 ? lon - 360 : lon < -180 ? lon + 360 : lon

    // Clamp latitude
    const clampedLat = Math.max(-90, Math.min(90, lat))

    neighbors.push(encodeGeohash(clampedLat, wrappedLon, geohash.length))
  }

  return [...new Set(neighbors)] // Remove duplicates
}

/**
 * Check if a point is within a given radius of another point
 * @param center Center point
 * @param point Point to check
 * @param radiusKm Radius in kilometers
 * @returns True if point is within radius
 */
export function isWithinRadius(
  center: GeoLocation | null,
  point: GeoLocation | null,
  radiusKm: number
): boolean {
  if (!center || !point) {
    return false
  }
  return getDistance(center.latitude, center.longitude, point.latitude, point.longitude) <= radiusKm
}

/**
 * Format distance for display
 * @param distanceKm Distance in kilometers
 * @returns Formatted string (e.g., "500 m" or "2.5 km")
 */
export function formatDistance(distanceKm: number): string {
  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)} m`
  }
  return `${distanceKm.toFixed(1)} km`
}
